import type { ButtonPropSize, ButtonPropView } from 'uikit';
import { Button, Grid, GridItem, IconClose, Loader, Modal, Text, useDebounce } from 'uikit';

import cn from 'classnames';
import type { Property } from 'csstype';
import type { ComponentProps, FC, ReactChild } from 'react';
import { useCallback, useMemo } from 'react';

import { useContainerDock } from './hooks/useContainerDock';

import styles from './ModalDialog.module.css';

export interface IModalDialogProps {
    title: ReactChild;
    onClose: () => void;
    onConfirm?: () => void;
    btnConfirmLabel?: string;
    btnConfirmDisabled?: boolean;
    btnCancelLabel?: string;
    btnCancelView?: ButtonPropView;
    isCommit?: boolean;
    withTitleCloseButton?: boolean;
    withBottomButtons?: boolean;
    leftPanel?: any;
    isLoading?: boolean;
    isOnClickOutsideDisable?: boolean;
    isDockedToContainer?: boolean;
    containerRef?: React.MutableRefObject<null | HTMLElement>;
    dockedToWindow?: 'left' | 'right' | 'top' | 'bottom';
    bodyMinHeight?: string;
    bodyMaxHeight?: string;
    bodyOverflow?: Property.Overflow;
    bodyWidth?: number;
    status?: 'success' | 'error';
    buttonSize?: ButtonPropSize;
}

const invisibleClassName = 'invisible-tooltip';

const onBodyScroll = (e: any) => {
    const { top, height } = e.target.getBoundingClientRect();
    const bottom = top + height;

    Array.from(document.getElementsByClassName('Tooltip')).forEach((tooltip) => {
        const tooltipTop = tooltip.getBoundingClientRect().top;

        if (tooltipTop < top || tooltipTop > bottom) {
            if (!tooltip.classList.contains(invisibleClassName)) {
                tooltip.classList.add(invisibleClassName);
            }
        } else if (tooltip.classList.contains(invisibleClassName)) {
            tooltip.classList.remove(invisibleClassName);
        }
    });
};

export const ModalDialog: FC<IModalDialogProps & Omit<ComponentProps<typeof Modal>, 'title'>> = (props) => {
    const {
        title,
        onConfirm,
        onClose,
        btnCancelLabel = 'Отменить',
        btnCancelView = 'ghost',
        btnConfirmLabel = 'Сохранить',
        btnConfirmDisabled,
        isCommit,
        children,
        withTitleCloseButton = false,
        leftPanel,
        isLoading = false,
        isOnClickOutsideDisable = false,
        isDockedToContainer,
        rootClassName,
        containerRef,
        className,
        bodyMinHeight,
        bodyMaxHeight,
        bodyWidth,
        bodyOverflow,
        status,
        withBottomButtons = true,
        dockedToWindow,
        buttonSize = 's',
        ...modalOptions
    } = props;

    const handleClickOutside = useCallback(() => {
        if (!isOnClickOutsideDisable) {
            onClose();
        }
    }, [isOnClickOutsideDisable, onClose]);

    const handleClickConfirm = useCallback(() => {
        if (onConfirm) {
            onConfirm();
        }
    }, [onConfirm]);

    const handleClickCancel = useCallback(() => {
        onClose();
    }, [onClose]);

    const actualRootClassName = useContainerDock(props);

    const bodyStyle = useMemo(
        () => ({
            overflow: bodyOverflow ?? 'auto',
            minHeight: bodyMinHeight ?? 200,
            maxHeight: bodyMaxHeight ?? '80%',
            ...(bodyWidth ? { width: bodyWidth } : {}),
        }),
        [bodyMaxHeight, bodyMinHeight, bodyOverflow, bodyWidth],
    );

    const debouncedBodyScroll = useDebounce(onBodyScroll, 20);

    return (
        <Modal
            rootClassName={actualRootClassName}
            {...modalOptions}
            onClickOutside={handleClickOutside}
            onEsc={onClose}
            className={cn(styles.rootModal, className, {
                [styles.success]: status === 'success',
                [styles.error]: status === 'error',
                [styles.loading]: isLoading,
            })}
        >
            {isLoading ? (
                <Loader size="m" style={{ position: 'absolute' }} />
            ) : (
                <>
                    <div className={styles.header}>
                        {typeof title === 'object' ? (
                            title
                        ) : (
                            <Text view="secondary" lineHeight="l" size="s" weight="semibold" transform="uppercase">
                                {title}
                            </Text>
                        )}

                        {withTitleCloseButton && handleClickCancel && (
                            <Button
                                onClick={handleClickCancel}
                                label="Закрыть"
                                iconSize="s"
                                iconLeft={IconClose}
                                view="clear"
                                onlyIcon
                            />
                        )}
                    </div>
                    <div className={styles.body} style={bodyStyle} onScroll={debouncedBodyScroll}>
                        {children}
                    </div>
                    {(leftPanel || withBottomButtons) && (
                        <div className={`footer ${styles.footer}`}>
                            <Grid cols={2}>
                                <GridItem className={styles.leftPanel}>{leftPanel}</GridItem>
                                {withBottomButtons && (
                                    <GridItem className={styles.buttons}>
                                        {onConfirm && (
                                            <Button
                                                onClick={handleClickConfirm}
                                                size={buttonSize}
                                                label={btnConfirmLabel}
                                                loading={isCommit}
                                                disabled={btnConfirmDisabled}
                                            />
                                        )}
                                        <Button
                                            onClick={handleClickCancel}
                                            size={buttonSize}
                                            label={btnCancelLabel}
                                            view={btnCancelView}
                                        />
                                    </GridItem>
                                )}
                            </Grid>
                        </div>
                    )}
                </>
            )}
        </Modal>
    );
};

ModalDialog.displayName = 'ModalDialog';
